import React, { useState } from "react";
import { FaArrowRight, FaGear, FaShieldHalved, FaMobileScreen, FaChartLine } from "react-icons/fa6";
import { BTN_SIZE, Container, H1_SIZE } from "../home/ui";

// Hero artwork (store on a laptop + phone). On desktop (xl+) it sits as a background on the right side
// of the section; on mobile/tablet it shows below the text.
// Put the file at public/img/ecommerce-hero.jpg (or change the path). Until it exists, the
// custom-website hero art is shown instead.
const heroImg = "/img/custom-website-hero1.jpg";
const fallbackImg = "/img/custom-website-hero.jpg";

const features = [
  { icon: FaGear, title: "Custom Design", text: "& Development" },
  { icon: FaShieldHalved, title: "Secure Payment", text: "Integration" },
  { icon: FaMobileScreen, title: "Mobile Responsive", text: "Shopping Experience" },
  { icon: FaChartLine, title: "SEO Optimized", text: "for Better Sales" },
];

// Fades all four edges of the image so its own background never shows as a box.
const fadeX = "linear-gradient(to right, transparent 0%, #000 10%, #000 94%, transparent 100%)";
const fadeY = "linear-gradient(to bottom, transparent 0%, #000 8%, #000 92%, transparent 100%)";
const edgeFade = {
  WebkitMaskImage: `${fadeX}, ${fadeY}`,
  WebkitMaskComposite: "source-in",
  maskImage: `${fadeX}, ${fadeY}`,
  maskComposite: "intersect",
};

const EcomHero = () => {
  const [src, setSrc] = useState(heroImg);
  const onError = () => src !== fallbackImg && setSrc(fallbackImg);

  return (
    <section className="relative overflow-hidden bg-gradient-to-r from-[#FCFCFE] via-[#F8F9FF] to-[#F1F3FE] pb-[48px] pt-[110px] xl:pb-[44px] xl:pt-[calc(clamp(76px,5.83vw,112px)+28px)]">
      {/* Desktop: artwork as background on the right (natural ratio, never stretched or cropped) */}
      <img
        src={src}
        onError={onError}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-[calc(clamp(76px,5.83vw,112px)+4px)] hidden h-[min(34vw,640px)] w-auto max-w-none select-none xl:block"
        style={edgeFade}
      />

      <Container className="relative grid max-w-[1900px] items-center gap-[32px] xl:grid-cols-[minmax(0,42%)_1fr] xl:pl-[clamp(32px,4.375vw,84px)] xl:pr-[clamp(24px,3.125vw,60px)]">
        <div className="m-hero">
          <span className="inline-flex items-center rounded-full bg-[#ECEFFF] px-[16px] py-[7px] text-[12px] font-medium uppercase tracking-[0.06em] text-[#3B3F8F]">
            E-commerce Development
          </span>

          <h1 className={`mt-[18px] font-bold tracking-tight text-[#0B0D2C] ${H1_SIZE}`}>
            <span className="whitespace-nowrap">E-commerce</span>
            {" "}<br />
            <span className="font-medium">Websites That</span>
            {" "}<br />
            <span className="text-[#7C2CF0]">Sell More</span>.
          </h1>

          <p className="mt-[20px] max-w-[560px] text-[16px] leading-[1.6] text-slate-600 sm:text-[17px] xl:text-[clamp(15px,1.05vw,18px)]">
            We build powerful, scalable, and user-friendly e-commerce websites that help you showcase your products,
            provide seamless shopping experiences, and grow your business online.
          </p>

          <div className="m-btns mt-[28px] flex flex-wrap items-center gap-[16px]">
            <a
              href="/contact-us"
              className={`group inline-flex items-center rounded-full bg-gradient-to-r from-[#0EA5FF] via-[#3D5BF6] to-[#8B2CF5] font-medium text-white shadow-[0_14px_30px_-14px_rgba(91,61,245,0.7)] transition hover:brightness-110 ${BTN_SIZE}`}
            >
              Get a Free Consultation
              <FaArrowRight className="text-[14px] transition group-hover:translate-x-1" />
            </a>
            <a
              href="/our-completed-projects"
              className={`inline-flex items-center rounded-full border-[1.5px] border-solid border-[#7FA3FF] bg-white/70 font-medium text-[#1E4DFF] transition hover:bg-[#EEF3FF] ${BTN_SIZE}`}
            >
              View Our Work
            </a>
          </div>
        </div>

        {/* Mobile / tablet: artwork below the text */}
        <div className="flex justify-center xl:hidden">
          <img
            src={src}
            onError={onError}
            alt="E-commerce store shown on a laptop and a phone"
            className="h-auto w-full max-w-[760px] select-none object-contain"
            style={edgeFade}
          />
        </div>
      </Container>

      {/* Feature strip */}
      <Container className="relative mt-[30px] max-w-[1900px] xl:pl-[clamp(32px,4.375vw,84px)] xl:pr-[clamp(24px,3.125vw,60px)]">
        <div className="m-swipe grid max-w-[1000px] grid-cols-1 gap-[18px] sm:grid-cols-2 lg:grid-cols-4 lg:gap-[14px] xl:max-w-[min(1000px,66%)]">
          {features.map((f) => (
            <div key={f.title} className="flex min-w-0 items-center gap-[12px]">
              <span className="inline-flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#EAF0FF] to-[#F1EBFF] text-[18px] text-[#3D5BF6] shadow-[0_6px_16px_-8px_rgba(61,91,246,0.45)]">
                <f.icon />
              </span>
              <div className="min-w-0">
                <p className="text-[14px] font-semibold leading-[1.3] text-[#0B1B4D] xl:text-[clamp(12.5px,0.92vw,14.5px)]">{f.title}</p>
                <p className="mt-[2px] text-[13px] leading-[1.4] text-slate-500 xl:text-[clamp(12px,0.88vw,13.5px)]">{f.text}</p>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
};

export default EcomHero;
